/**
 * Die Blätter „Zähler anlegen" und „Zähler bearbeiten" (Spec 46a, „Blatt") —
 * Bauform `BuildingSheet` (Anlegen: Hauptfelder, „Weitere Angaben", drei
 * Fußknöpfe) und `MasterDataEditSheet` (Bearbeiten). Die Felder teilen sich
 * `MeterFormFields`; Rümpfe baut `meter-form.ts`.
 *
 * Drei Einstiege beim Anlegen: aus dem Reiter *Energie* mit wählbarem Haus
 * (`meter-create-house`, vorbelegt mit dem gefilterten Haus), am Haus mit festem
 * `meter-create-parent` (`fixedHouse`). Geprüft wird am Server; Feldfehler
 * (422) stehen am Feld, alles andere (403/409) als Formularfehler.
 */

import { useRef, useState } from 'react'
import type { RefObject, SubmitEvent } from 'react'

import { EntityPicker } from '@/components/EntityPicker'
import type { EntitySelection } from '@/components/EntityPicker'
import { FIELD_CLASS } from '@/components/FormField'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useCreateMeter, useMeterOptions, useUpdateMeter } from '@/features/energy/api'
import type { MeterDetail } from '@/features/energy/api'
import { MeterMoreFields, MeterPrimaryFields } from '@/features/energy/MeterFormFields'
import { toErrorState } from '@/features/energy/form-error'
import type { ErrorState } from '@/features/energy/form-error'
import { canSaveMeter, deriveMeterForm, EMPTY_METER_FORM, toMeterCreate, toMeterUpdate } from '@/features/energy/meter-form'
import type { MeterFormValues } from '@/features/energy/meter-form'

/** `ALLOWED_PARENT_KINDS["meter"]` (`shared/hierarchy.py`) — ein Zähler hängt an
 * einem Grundstück oder Gebäude. Bezeichnersatz für den Suchmodus des Pickers,
 * keine Fachregel (der Server prüft). */
const HOUSE_KINDS = ['property', 'building'] as const

/** Felder der zweiten Gruppe: Ein Fehler dort klappt „Weitere Angaben" auf. */
const MORE_FIELDS = new Set(['digits', 'tariff', 'operator', 'reading_interval_days'])

function fieldSetter(setForm: (update: (current: MeterFormValues) => MeterFormValues) => void) {
  return (name: keyof MeterFormValues) => (value: string) => {
    setForm((current) => ({ ...current, [name]: value }))
  }
}

interface MeterCreateSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Haus ist gesetzt und nicht änderbar (Weg „am Objekt"). */
  readonly fixedHouse?: EntitySelection
  /** Vorbelegung, die sich ändern lässt (Weg „aus dem Reiter"). */
  readonly presetHouse?: EntitySelection
  /** Nach jedem erfolgreichen Anlegen, auch bei „und noch eins". */
  readonly onCreated?: () => void
}

export function MeterCreateSheet({ open, onOpenChange, fixedHouse, presetHouse, onCreated }: MeterCreateSheetProps) {
  const createMutation = useCreateMeter()
  const options = useMeterOptions(open)
  const serialRef = useRef<HTMLInputElement>(null)
  const initialHouse = fixedHouse ?? presetHouse ?? null

  const [form, setForm] = useState<MeterFormValues>(EMPTY_METER_FORM)
  const [house, setHouse] = useState<EntitySelection | null>(initialHouse)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [errors, setErrors] = useState<ErrorState>({})
  const setField = fieldSetter(setForm)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop
  // changes", wie `BuildingSheet`).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(EMPTY_METER_FORM)
      setHouse(initialHouse)
      setIsMoreOpen(false)
      setErrors({})
    }
  }

  const canSave = canSaveMeter(house?.id, form) && !createMutation.isPending

  async function submit(andAnother: boolean) {
    if (house === null) return
    setErrors({})
    try {
      await createMutation.mutateAsync(toMeterCreate(house.id, form))
      onCreated?.()
      if (andAnother) {
        // Haus bleibt, Art und Nummer sind leer, die Vorgaben kehren zurück.
        setForm(EMPTY_METER_FORM)
        setIsMoreOpen(false)
        serialRef.current?.focus()
      } else {
        onOpenChange(false)
      }
    } catch (error) {
      const next = toErrorState(error)
      if (next.field !== undefined && MORE_FIELDS.has(next.field.field)) setIsMoreOpen(true)
      setErrors(next)
    }
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          serialRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={handleSubmit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Zähler anlegen</SheetTitle>
            {fixedHouse !== undefined && (
              <p data-testid="meter-create-parent" className="text-label text-muted">
                {fixedHouse.label}
              </p>
            )}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {fixedHouse === undefined && (
              <EntityPicker
                testId="meter-create-house"
                label="Haus"
                apiField="house_id"
                errorPrefix="meter-create"
                value={house}
                onSelect={setHouse}
                error={errors.field}
                searchable
                kinds={HOUSE_KINDS}
                searchPlaceholder="Haus suchen"
                placeholder="Haus wählen"
              />
            )}

            <MeterPrimaryFields
              prefix="meter-create"
              form={form}
              onChange={setField}
              error={errors.field}
              options={options.data}
              isOptionsPending={options.isPending}
              serialRef={serialRef}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="meter-create-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>
              <div className="flex flex-col gap-4 pt-2">
                <MeterMoreFields
                  prefix="meter-create"
                  form={form}
                  onChange={setField}
                  error={errors.field}
                  tariffs={options.data?.tariffs ?? []}
                  isOptionsPending={options.isPending}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {errors.general !== undefined && (
              <p data-testid="meter-create-error" role="alert" className="text-label text-status-overdue">
                {errors.general}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="meter-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="meter-create-save-another"
                disabled={!canSave}
                aria-busy={createMutation.isPending}
                onClick={() => void submit(true)}
                className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
              >
                Anlegen und noch eins
              </button>
              <button
                type="submit"
                data-testid="meter-create-save"
                disabled={!canSave}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Zähler anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

interface MeterEditSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly meter: MeterDetail
  readonly triggerRef: RefObject<HTMLButtonElement | null>
  readonly onSaved: () => void
}

export function MeterEditSheet({ open, onOpenChange, meter, triggerRef, onSaved }: MeterEditSheetProps) {
  const updateMutation = useUpdateMeter()
  const options = useMeterOptions(open)
  const [form, setForm] = useState<MeterFormValues>(() => deriveMeterForm(meter))
  const [errors, setErrors] = useState<ErrorState>({})
  const setField = fieldSetter(setForm)

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(deriveMeterForm(meter))
      setErrors({})
    }
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(deriveMeterForm(meter))

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setErrors({})
    try {
      await updateMutation.mutateAsync({
        meterId: meter.id,
        body: toMeterUpdate(meter, form),
        ifMatch: String(meter.version),
      })
      onOpenChange(false)
      onSaved()
    } catch (error) {
      setErrors(toErrorState(error))
    }
  }

  return (
    <MasterDataEditSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Zähler bearbeiten"
      triggerRef={triggerRef}
      formId="meter-edit-form"
      saveTestId="meter-edit-save"
      saveDisabled={!isDirty || form.serial.trim() === '' || updateMutation.isPending}
      isSaving={updateMutation.isPending}
    >
      <form
        id="meter-edit-form"
        onSubmit={(event) => void handleSave(event)}
        className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4"
      >
        <MeterPrimaryFields
          prefix="meter-edit"
          form={form}
          onChange={setField}
          error={errors.field}
          options={undefined}
          isOptionsPending={false}
          kindText={`${meter.kind.label} · ${meter.unit}`}
        />
        <MeterMoreFields
          prefix="meter-edit"
          form={form}
          onChange={setField}
          error={errors.field}
          tariffs={options.data?.tariffs ?? [{ value: meter.tariff.value, label: meter.tariff.label }]}
          isOptionsPending={options.isPending && options.fetchStatus !== 'idle'}
        />
        {errors.general !== undefined && (
          <p data-testid="meter-edit-error" role="alert" className="text-label text-status-overdue">
            {errors.general}
          </p>
        )}
      </form>
    </MasterDataEditSheet>
  )
}
